import { useState } from "react";
import { Link } from "react-router-dom";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";

const FAQS = [
    {
        q: "How does a term deposit work?",
        a: "You deposit USDC into a fixed 3-year term earning 20% simple annual interest, accrued daily in USDC and withdrawable at any time. Each deposit routes atomically in one transaction: 80% to the Safe Vault (structurally quarantined principal), 10% to an immediate $GILD buy on the open market, 9% to the Distribution Reserve (yield), and 1% to a liquidity top-up."
    },
    {
        q: "Is the 20% yield guaranteed?",
        a: "No yield anywhere is 100% guaranteed. The 20% is a target rate, paid from real USDC profits the treasury generates — not from printing tokens — and is subject to available protocol reserves. Your 80% Safe Vault principal plus any accrued interest is not at risk from treasury performance."
    },
    {
        q: "Where does the 20% yield come from?",
        a: "Every deposit's 10% slice buys $GILD on the open market, pushing the price above its treasury-backed value (TVG). The treasury sells a small slice at that premium and uses the USDC to pay yield. As the pool deepens it shifts to steady low-slippage sales near TVG. Proceeds run through a waterfall that funds ~6 months of yield coverage first, then the buyback reserve, then surplus."
    },
    {
        q: "Is my principal structurally quarantined?",
        a: "Yes. The 80% Safe Vault slice is segregated: it cannot be touched by treasury operations, liquidity, buybacks or governance. Only your own deposit's lifecycle flows (maturity, early exit, liquidation, or a loan you authorise) can move it. Using Leverage or Turbo changes how much of that Safe capital is accessible if you exit early."
    },
    {
        q: "What's the difference between Standard, Leverage and Turbo?",
        a: "GILDer is really one product — every deposit uses the same 80 / 10 / 9 / 1 split. The modes differ only in what you do with the 80% Safe capital: Standard leaves it untouched for 3 years; Leverage lets you borrow up to 75% of it; Turbo automatically borrows the maximum and re-deposits it repeatedly for higher yield."
    },
    {
        q: "What is Turbo?",
        a: "A one-click leveraged deposit: it borrows against your Safe Vault, re-deposits, and repeats up to about 2.5×, so net yield can reach up to 27.5% on your equity. The full 27.5% needs roughly $10,000+ deposited; smaller deposits earn less (down to ~11% at the $100 minimum). There is no price-based liquidation, but exiting before maturity returns about 50% of your original capital plus accrued interest, because the Safe capital has been reused."
    },
    {
        q: "What is Leverage?",
        a: "On a Standard deposit you can borrow up to 75% of your Safe Vault for a flat 15% p/a fee. You keep earning 20% on the full principal while paying 15% on what you borrow, so a maximum borrow nets about 11% p/a on the full deposit while freeing up cash. There's no penalty for not repaying — at maturity there's simply less left in your Safe."
    },
    {
        q: "What happens if I exit early?",
        a: "On Standard, early exit before maturity carries a 20% penalty of principal and you keep all accrued interest. On Turbo it's higher — you receive about 50% of your original capital plus accrued interest — because the Safe capital was leveraged. Penalties strengthen the Distribution Reserve. The Phase 2 Marketplace will let you sell the deposit NFT instead of paying the penalty."
    },
    {
        q: "What is TVG?",
        a: "Treasury Value per $GILD — the treasury-backed floor value of the token, equal to Treasury USDC ÷ circulating supply. Treasury USDC is the USDC the protocol has generated (the liquidity-pool USDC above the initial seed, plus the Yield Reserve); circulating supply is the $GILD in the pool plus public wallets (treasury-held tokens are excluded). The treasury buys below TVG and sells above it, so the floor ratchets up as the protocol grows."
    },
    {
        q: "What is Automated Reinvestment?",
        a: "Once a deposit's accrued interest crosses 100 USDC it can be reinvested automatically into a fresh 3-year term deposit. Consolidated Reinvestment pools interest across all your deposits and sweeps it monthly, which compounds faster. Configure it per-deposit in Settings."
    },
    {
        q: "Are there any founder, team or VC tokens?",
        a: "No. There are no founder, team or VC allocations whatsoever. All 1 trillion $GILD were allocated at genesis to protocol-controlled wallets only — 50% Treasury, 30% Yield Treasury, 15% Marketing & Operations, 5% Liquidity Pool. No private wallet holds any tokens unless bought on the open market after launch."
    },
    {
        q: "Why can't I transfer my deposit NFT freely?",
        a: "Each deposit is an ERC-721C NFT with whitelist-gated, marketplace-only transfers and an enforced 5% royalty. This preserves royalty capture and compliance control. Direct transfers outside the authorised marketplace are not possible, and a pooled NFT is frozen until detached."
    },
    {
        q: "What happens after maturity?",
        a: "Interest stops accruing. You enter a 12-month grace period during which funds stay fully accessible with no extra charge. After that, an abandoned deposit incurs a 5% monthly maintenance fee on the remaining Safe Vault balance until withdrawn."
    },
    {
        q: "What if no one makes new deposits?",
        a: "The protocol doesn't break. Your 80% Safe Vault principal stays secure and withdrawable, and the TVG floor holds so the treasury can keep selling to fund yield. Yield may slow if treasury profits thin out. If it ever becomes truly unsustainable, an automatic Graceful Shutdown distributes the remaining treasury assets proportionally to deposit holders."
    }
];

/**
 * Help / Support — FAQ, a protocol-parameter reference table and key
 * contract addresses. Static, wallet-independent content.
 */
export default function Support() {
    const [open, setOpen] = useState(0);
    // Guests (no wallet) get the full-bleed public chrome (no sidebar, no
    // padded <main>), which stretches this app-style page edge-to-edge and
    // looks off. When there's no connected wallet we add our own centered
    // max-width container + padding so it reads the same as the connected
    // view. Connected users already sit inside the padded app <main>, so we
    // leave the layout to that and add nothing extra.
    const { address } = useWallet();
    const guest = !address;

    const params = [
        ["Term length", `${config.protocol.fixedTermDays} days (3 years)`],
        ["Annual yield (Standard)", `${config.protocol.annualRateBps / 100}% simple`],
        ["Turbo net yield", "up to 27.5%"],
        ["Minimum deposit", `${config.protocol.minDepositUsdc} USDC`],
        ["Capital routing", "80% Vault / 10% $GILD buy / 9% Distribution Reserve / 1% Liquidity"],
        ["Early-exit penalty", `${config.protocol.earlyExitPenaltyBps / 100}% Standard / ~50% Turbo`],
        ["Max borrow LTV", `${config.protocol.maxLtvBps / 100}%`],
        ["Loan APR", `${config.protocol.loanAprBps / 100}%`],
        ["Liquidation threshold", `${config.protocol.liquidationThresholdBps / 100}%`],
        ["Yield coverage target", "~6 months"],
        ["Automated Reinvestment trigger", `${config.protocol.autoCompoundTriggerUsdc} USDC`],
        ["NFT royalty", "5% (enforced on-chain)"]
    ];

    const contracts = [
        ["Bond", config.contracts.bond],
        ["Safe Vault", config.contracts.safeVault],
        ["Distribution Reserve", config.contracts.treasury],
        ["Deposit NFT", config.contracts.depositNft],
        ["Lending", config.contracts.lending],
        ["Turbo", config.contracts.turbo],
        ["Automated Reinvestment", config.contracts.autoCompound],
        ["Reinvestment", config.contracts.reinvestment]
    ].filter(([, addr]) => addr);

    return (
        <div
            className={`flex flex-col gap-3${
                guest ? " mx-auto w-full max-w-[1280px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8" : ""
            }`}
        >

            <div className="grid gap-3 lg:grid-cols-3">
                {/* FAQ */}
                <section className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 lg:col-span-2">
                    <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                        Frequently Asked Questions
                    </h3>
                    <ul className="mt-3 space-y-2">
                        {FAQS.map((item, i) => {
                            const expanded = open === i;
                            return (
                                <li
                                    key={item.q}
                                    className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b]"
                                >
                                    <button
                                        type="button"
                                        onClick={() => setOpen(expanded ? -1 : i)}
                                        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
                                    >
                                        <span className="font-manrope text-[12px] font-bold text-white">
                                            {item.q}
                                        </span>
                                        <span
                                            className={`shrink-0 text-[#FBC206] transition-transform ${
                                                expanded ? "rotate-45" : ""
                                            }`}
                                        >
                                            <PlusIcon />
                                        </span>
                                    </button>
                                    {expanded ? (
                                        <p className="border-t border-white/10 px-3 py-2.5 text-[11px] leading-relaxed text-[#e6c985]/80">
                                            {item.a}
                                        </p>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                </section>

                {/* Parameters → Deployed Contracts → Need more help (stacked) */}
                <section className="flex flex-col gap-3">
                    <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            Protocol Parameters
                        </h3>
                        <dl className="mt-2 space-y-1.5 text-[10.5px]">
                            {params.map(([label, value]) => (
                                <div
                                    key={label}
                                    className="flex items-start justify-between gap-3 border-b border-white/5 pb-1.5 last:border-0"
                                >
                                    <dt className="text-[#e6c985]/70">{label}</dt>
                                    <dd className="text-right font-semibold text-white">{value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    {contracts.length > 0 ? (
                        <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                            <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                                Deployed Contracts ({config.network.name})
                            </h3>
                            <div className="mt-2 grid gap-1.5">
                                {contracts.map(([label, addr]) => (
                                    <a
                                        key={label}
                                        href={`${config.network.blockExplorerUrl}/address/${addr}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center justify-between rounded-md bg-white/10 ring-1 ring-[#bfb67b] px-3 py-1.5 text-[10.5px] hover:bg-white/15 transition-colors"
                                    >
                                        <span className="font-semibold text-white">{label}</span>
                                        <span className="text-[#FBC206]">
                                            {addr.slice(0, 8)}…{addr.slice(-6)}
                                        </span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            Need more help?
                        </h3>
                        <p className="mt-1.5 text-[10.5px] leading-snug text-[#e6c985]/75">
                            Check live solvency on the health dashboard.
                        </p>
                        <div className="mt-2.5 flex flex-col gap-1.5">
                            <Link
                                to="/protocol-health"
                                className="rounded-md bg-[#162e1f] ring-1 ring-[#bfb67b] px-3 py-1.5 text-[11px] font-semibold text-[#e6c985] hover:bg-[#1d3a27] transition-colors"
                            >
                                Protocol Health →
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

function HelpIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12" y2="17" />
        </svg>
    );
}

function PlusIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <line x1="10" y1="4" x2="10" y2="16" />
            <line x1="4" y1="10" x2="16" y2="10" />
        </svg>
    );
}
